import React, { type ReactNode, useCallback, useContext, useMemo, useState } from 'react';

import { ErrorBoundary } from 'react-error-boundary';
// eslint-disable-next-line @atlassian/relay/use-single-relay-environment -- the trigger children must be re-pinned to the host environment, not the card's own one
import { ReactRelayContext, RelayEnvironmentProvider } from 'react-relay';

import type { PopupProps } from '@atlaskit/popup';
import {
	TeamsAppAnalyticsContext,
	useAnalyticsEvents,
} from '@atlaskit/teams-app-internal-analytics';
import {
	PopupTriggerWithHover,
	type TriggerMode,
} from '@atlaskit/teams-app-internal-popup-adaptor/popup-trigger-with-hover';
import { layers } from '@atlaskit/theme/constants';
import { AtlassianRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { firedAt } from '../analytics';
import { useFireTriggeredRequest } from '../hooks/useProfileCardAnalytics';
// oxlint-disable-next-line eslint-plugin-import(extensions) -- entrypoint is part of the entrypoint module name.
import {
	entryPoint,
	type UserProfileCardRelayTriggerParams,
} from '../network/userProfileCard.entrypoint';
import { CardOpenTimerProvider, useCardOpenTimer } from '../store/cardOpenTimer';
import { UserProfileCardContext } from '../store/context';
import type { TownsquareApp } from '../store/context';
import type { UserProfileCardAction } from '../types';
import { makeGetTestId } from '../utils/testId';

import { CardError } from './components/CardError';
import { CardLoading } from './components/CardLoading';
import { UserProfileCardTrigger } from './components/UserProfileCardTrigger';
import type { UserProfileCardRelayAgentProps } from './UserProfileCardRelay';

/**
 * Consumer-facing types re-exported so callers of the `./trigger` subpath can import them
 * without reaching into the package internals.
 */
export type { UserProfileCardAction } from '../types';

/**
 * Props for {@link UserProfileCardWithTrigger}. Extends {@link UserProfileCardRelayTriggerParams}
 * with popup placement, trigger mode, render target, and the trigger element children.
 */
export type UserProfileCardWithTriggerProps = UserProfileCardRelayTriggerParams &
	Pick<PopupProps, 'placement'> & {
		/** Hover, click, or preload-on-hover. Defaults to `'click'`. */
		trigger?: TriggerMode;
		/** When `true`, renders the popup into the document body via a portal. */
		shouldRenderToParent?: boolean;
		/** Element that the popup attaches to (the visible trigger). */
		children: ReactNode;
		/** Test id forwarded to the popup root. */
		testId?: string;
		/** Identifies the Townsquare app rendering this card (for XPC MAU tracking). */
		townsquareApp?: TownsquareApp;
		/**
		 * Called when the "View profile" button is clicked, in addition to the default navigation
		 * (e.g. for analytics). It does not replace the default navigation behaviour.
		 */
		onProfileClick?: (userId: string) => void;
		/** Agent-card config. Required for agent subjects. Omitting renders `CardError` if the subject is an agent. */
		agent?: UserProfileCardRelayAgentProps;
		/**
		 * Consumer-supplied custom actions exposed via the action menu in the
		 * top-right of the card header. Mirrors the `actions` prop on legacy `@atlaskit/profilecard`.
		 */
		actions?: UserProfileCardAction[];
		/**
		 * When `true`, hides the trigger from assistive tech (`aria-hidden` + `tabIndex={-1}`) while
		 * keeping pointer interaction. Use on a secondary trigger (e.g. an avatar) whose subject is
		 * already announced by an adjacent trigger. Mirrors legacy `@atlaskit/profilecard`.
		 */
		ariaHideProfileTrigger?: boolean;
		/**
		 * When `false`, renders the children without the interactive card (no popup, no preload).
		 * Defaults to visible. Mirrors the `isVisible` prop on legacy `@atlaskit/profilecard`.
		 */
		isVisible?: boolean;
		/**
		 * Accessible name for the trigger element, announced by screen readers. When omitted, a
		 * generic fallback ("More information about this user") is used.
		 */
		ariaLabel?: string;
	};

/**
 * Opens the user profile card in a popup (hover / click / preload) using the same Relay entry-point
 * pattern as `TeamProfileCardWithTrigger` (`@atlassian/team-profilecard/trigger`): preload runs
 * `UserProfileCardEntryPointQuery` (`knowledgeDiscovery.singleUser` + `activity.workedOn`).
 *
 * For a presentational card with data you already have, use {@link UserProfileCard} directly.
 */
export function UserProfileCardWithTrigger({
	isVisible,
	...props
}: UserProfileCardWithTriggerProps): React.JSX.Element {
	const hostRelayEnvironment = useContext(ReactRelayContext)?.environment;

	if (isVisible === false) {
		return <>{props.children}</>;
	}

	return (
		<AtlassianRelayEnvironmentProvider>
			<UserProfileCardWithTriggerInner {...props} hostRelayEnvironment={hostRelayEnvironment} />
		</AtlassianRelayEnvironmentProvider>
	);
}

/** Inner implementation of {@link UserProfileCardWithTrigger}. */
function UserProfileCardWithTriggerInner({
	hostRelayEnvironment,
	userId,
	cloudId,
	orgId,
	viewerId,
	trigger,
	placement = 'bottom-start',
	shouldRenderToParent = false,
	children,
	testId,
	townsquareApp,
	onProfileClick,
	agent,
	actions,
	ariaHideProfileTrigger,
	ariaLabel,
}: UserProfileCardWithTriggerProps & {
	hostRelayEnvironment?: NonNullable<React.ContextType<typeof ReactRelayContext>>['environment'];
}): React.JSX.Element {
	const resolvedTriggerMode = trigger ?? 'click';

	const relayWrappedChildren = hostRelayEnvironment ? (
		<RelayEnvironmentProvider environment={hostRelayEnvironment}>
			{children}
		</RelayEnvironmentProvider>
	) : (
		children
	);

	const entryPointErrorFallback = useCallback(
		(_props: { error: Error; onClose: () => void }) => <CardError testId={testId} />,
		[testId],
	);

	const getTestId = useMemo(() => makeGetTestId(userId, testId), [userId, testId]);

	// `triggerMethod` is the actual open method ('click' | 'hover') reported by the popup
	// adapter. It stays `undefined` until the first open so we never report a trigger-mode
	// value (e.g. 'preload-on-hover') as if it were an open method. This is hoisted onto
	// `TeamsAppAnalyticsContext.attributes` below so every analytics event fired beneath
	// the popup picks it up automatically without each event having to attach it manually.
	const [triggerMethod, setTriggerMethod] = useState<'click' | 'hover' | undefined>(undefined);

	const contextValue = useMemo(
		() => ({
			cloudId: cloudId ?? '',
			townsquareApp,
			onProfileClick,
			getTestId,
		}),
		[cloudId, townsquareApp, onProfileClick, getTestId],
	);

	const analyticsContextData = useMemo(
		() => ({
			source: 'userProfileCard' as const,
			attributes: {
				cloudId: cloudId ?? '',
				userId,
				viewerId: viewerId ?? '',
				// Only attach `method` once an actual open has occurred so SLO dashboards never
				// see a synthetic method on pre-open events (e.g. preload).
				...(triggerMethod !== undefined ? { method: triggerMethod } : {}),
			},
		}),
		[cloudId, viewerId, userId, triggerMethod],
	);

	if (!userId) {
		return <>{relayWrappedChildren}</>;
	}

	return (
		<ErrorBoundary fallback={<>{relayWrappedChildren}</>}>
			<UserProfileCardContext.Provider value={contextValue}>
				<TeamsAppAnalyticsContext data={analyticsContextData}>
					<CardOpenTimerProvider>
						<UserProfileCardPopupWithAnalytics
							entryPointParams={{ userId, cloudId, orgId, viewerId }}
							entryPointProps={{
								cloudId: cloudId ?? '',
								viewerId,
								testId,
								agent,
								actions,
							}}
							placement={placement}
							shouldRenderToParent={shouldRenderToParent}
							triggerMode={resolvedTriggerMode}
							errorFallback={entryPointErrorFallback}
							onTriggerMethodChange={setTriggerMethod}
							trigger={(triggerProps) => (
								<UserProfileCardTrigger
									trigger={resolvedTriggerMode}
									triggerProps={triggerProps}
									ariaHideProfileTrigger={ariaHideProfileTrigger}
									ariaLabel={ariaLabel}
								>
									{relayWrappedChildren}
								</UserProfileCardTrigger>
							)}
						/>
					</CardOpenTimerProvider>
				</TeamsAppAnalyticsContext>
			</UserProfileCardContext.Provider>
		</ErrorBoundary>
	);
}

function UserProfileCardPopupWithAnalytics({
	entryPointParams,
	entryPointProps,
	placement,
	shouldRenderToParent,
	triggerMode,
	errorFallback,
	onTriggerMethodChange,
	trigger,
}: {
	entryPointParams: UserProfileCardRelayTriggerParams;
	entryPointProps: {
		cloudId: string;
		viewerId?: string;
		testId?: string;
		agent?: UserProfileCardRelayAgentProps;
		actions?: UserProfileCardAction[];
	};
	placement: PopupProps['placement'];
	shouldRenderToParent: boolean;
	triggerMode: TriggerMode;
	errorFallback: (props: { error: Error; onClose: () => void }) => React.JSX.Element;
	/** Bubbles the trigger method up to the parent so it can be exposed via UserProfileCardContext. */
	onTriggerMethodChange: (method: 'click' | 'hover') => void;
	trigger: React.ComponentProps<typeof PopupTriggerWithHover>['trigger'];
}): React.JSX.Element {
	const { fireEvent } = useAnalyticsEvents();
	const { markInteractionStart } = useCardOpenTimer();
	const fireTriggeredRequest = useFireTriggeredRequest();

	const handleOpen = useCallback(
		(method: 'click' | 'hover') => {
			// t=0 for all downstream `duration` measurements.
			markInteractionStart();
			onTriggerMethodChange(method);
			// `method` is attached inline because the context update from
			// `onTriggerMethodChange` has not propagated yet at this exact moment.
			fireEvent('ui.profilecard.triggered', { firedAt: firedAt(), method });
			fireTriggeredRequest();
		},
		[fireEvent, fireTriggeredRequest, markInteractionStart, onTriggerMethodChange],
	);

	return (
		<PopupTriggerWithHover
			entryPoint={entryPoint}
			placement={placement}
			fallback={<CardLoading testId={entryPointProps.testId ?? ''} />}
			errorFallback={errorFallback}
			entryPointParams={entryPointParams}
			entryPointProps={entryPointProps}
			shouldRenderToParent={shouldRenderToParent}
			shouldUseCaptureOnOutsideClick={true}
			triggerMode={triggerMode}
			onOpen={handleOpen}
			trigger={trigger}
			zIndex={layers.modal()}
		/>
	);
}
